import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { XCircleIcon } from "@heroicons/react/24/solid";

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const [liveDate, setLiveDate] = useState("");

  const handleClose = () => {
    navigate(-1); // Navigate back to the previous page
  };

  // Function to get the live date
  useEffect(() => {
    const currentDate = new Date().toLocaleDateString("en-GB"); // Format: DD/MM/YYYY
    setLiveDate(currentDate);
  }, []);

  return (
     <div className="bg-gradient-to-r from-gray-100 to-gray-200 py-12 px-6 sm:px-8 lg:px-10 relative text-sm">
      {/* Close Icon */}
      <button onClick={handleClose} className="absolute top-6 right-8 text-gray-700 hover:text-red-500 transition">
        <XCircleIcon className="w-8 h-8" />
      </button>

      <div className="text-sm max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
        {/* Header Section */}
        <header className="bg-gradient-to-r from-blue-500 to-teal-400 p-6 text-white text-center relative">
          <h1 className="text-4xl font-extrabold mb-2">Terms and Conditions</h1>
          <p className="text-sm font-light">
            Effective Date: <strong>{liveDate}</strong> {/* Displays current date dynamically */}
          </p>
        </header>

        <div className="p-8">
          {/* Section: Disclaimer */}
          <section className="mb-8">
            <h2 className="text-sm font-semibold text-gray-800 border-b-2 border-blue-500 mb-4 pb-2">
              Disclaimer
            </h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              The information, services, and products available on BuildPro-OGCS Private Limited’s online platform are provided "as is" for general informational purposes only. While we strive to provide accurate and up-to-date information, BuildPro-OGCS Private Limited does not guarantee the accuracy, completeness, or reliability of any content or materials on the platform.
            </p>
            <p className="text-gray-700 text-sm leading-relaxed">
              BuildPro-OGCS acts solely as a facilitator between sellers and buyers. The company does not endorse, guarantee, or warrant any products or services offered by third-party sellers on the platform. All transactions between sellers and buyers are exclusively between the involved parties. BuildPro-OGCS shall not be held liable for any disputes, damages, or losses arising from such transactions.
            </p>
            <p className="text-gray-700 text-sm leading-relaxed">
              To the fullest extent permitted by law, BuildPro-OGCS shall not be liable for any direct, indirect, incidental, consequential, or special damages arising from the use of the platform. By using this platform, both sellers and buyers acknowledge that they will perform their own due diligence and assume all risks associated with their respective transactions.
            </p>
          </section>

    {/* Section: Terms and Conditions */}
    <section className="mb-8">
            <h2 className="text-sm font-semibold text-gray-800 border-b-2 border-blue-500 mb-4 pb-2">
              Terms and Conditions for BuildPro OGCS Private Limited
            </h2>
            <h3 className="text-sm font-medium text-gray-800 mb-3">1. Compliance with Indian Laws</h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              All transactions and operations conducted through BuildPro OGCS Private Limited are governed by the relevant Indian laws, including but not limited to the Information Technology Act, 2000, and the Consumer Protection Act, 2019.
            </p>

            <h3 className="text-sm font-medium text-gray-800 mb-3">2. Jurisdiction</h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              Any disputes arising from transactions or operations on the BuildPro OGCS Private Limited platform will be subject to the jurisdiction of courts located in Mumbai, Delhi, or Bangalore, as applicable.
            </p>

            <h3 className="text-sm font-medium text-gray-800 mb-3">3. Governing Law</h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              The terms and conditions of this platform are governed by the Indian Contract Act, 1872.
            </p>

            <h3 className="text-sm font-medium text-gray-800 mb-3">Additional Terms for B2C Transactions</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li><strong>Consumer Protection Notice:</strong> We are committed to ensuring consumer protection in all transactions. For any grievances or complaints, please contact our customer service team.</li>
              <li><strong>Return and Refund Policy:</strong>
                <ul className="list-disc list-inside pl-6">
                  <li>Returns are accepted within 10 days of receipt, only for defective or damaged products.</li>
                  <li>Sellers must not refuse returns for defective, delayed, or mismatched items. Customers can return durable or semi-durable items within 15 days for a refund.</li>
                  <li>Refunds will be processed to the original payment method within 15 days after the return is received by the seller.</li>
                  <li>Products must be in their original condition and packaging.</li>
                </ul>
              </li>
              <li><strong>Exchange Policy:</strong>
                <ul className="list-disc list-inside pl-6">
                  <li>This platform specializes in construction materials, tools, tackles, and equipment, including high-volume items. Returns for high-volume items are not possible due to their specific nature.</li>
                  <li>Small items within their expiry date can be exchanged if in their original condition and packaging.</li>
                  <li>Eligible exchanges must be made within 10 days of receipt.</li>
                  <li>Returned products will be verified, and exchanged products will be shipped once the return is confirmed.</li>
                </ul>
              </li>
              <li><strong>Warranty and Support Terms:</strong> Warranty terms are provided by manufacturers and vary by product. Contact the manufacturer's service desk for issues.</li>
              <li><strong>User Account Terms:</strong> Users are responsible for maintaining the confidentiality of their account credentials and providing accurate and up-to-date information during account creation and use.</li>
            </ul>

            <h3 className="text-sm font-medium text-gray-800 mb-3">Terms for B2B Transactions</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li><strong>Bulk Order Cancellation:</strong> Cancellation is not permitted once order preparation has commenced or after manufacturing has started for made-to-order products.</li>
            </ul>
          </section>

   {/* Section: Refund Terms and Conditions */}
   <section className="mb-8">
            <h2 className="text-sm font-semibold text-gray-800 border-b-2 border-blue-500 mb-4 pb-2">
              Refund Terms and Conditions
            </h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li><strong>Cancellation Before Seller Confirmation:</strong> If the buyer cancels the order before it is confirmed by the seller, a full refund will be issued.</li>
              <li><strong>Cancellation After Seller Confirmation:</strong> If the buyer cancels the order after it has been confirmed by the seller, a refund will be provided, excluding any applicable delivery charges.</li>
              <li><strong>Cancellation After Shipment:</strong> If the buyer cancels the order after it has been shipped, a refund will be issued after deducting double the delivery charges.</li>
              <li><strong>Material Rejection or Specification Mismatch:</strong> If the order is canceled due to material rejection or a mismatch in specifications as per the order confirmation, the buyer will receive a full refund of the order amount.</li>
              <li><strong>Order Cancellation or Rejection Timeframe:</strong> The buyer may cancel or reject the order within 3 days of receiving the product. Cancellations or rejections made outside this timeframe may not be eligible for a refund.</li>
              <li><strong>Order Delivery and Unloading at Buyer’s Place:</strong> Packing, loading, and transportation to the seller's address will be the buyer’s responsibility. The buyer must bear all costs associated with packing, loading, and transportation.</li>
              <li><strong>Initiation of Refund:</strong> Refunds will be initiated within 10 working days after the seller confirms the material has been received in its original condition. If the material is damaged, or if items are missing or if only partial delivery was made, the balance will be refunded after recovery of the missing items.</li>
            </ul>
            <h3 className="text-sm font-medium text-gray-800 mt-6 mb-3">Note:</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>This refund policy applies only to materials, goods, tools, and equipment. A separate policy applies for services provided to sellers by BuildPro-OGCS.</li>
              <li>Any disputes between the seller and buyer must be resolved mutually. If resolution cannot be achieved, the dispute will be subject to Mumbai jurisdiction.</li>
            </ul>
          </section>


          {/* Section: Privacy Policy */}
          <section className="mb-8">
            <h2 className="text-sm font-semibold text-gray-800 border-b-2 border-blue-500 mb-4 pb-2">
              Privacy Policy
            </h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              At BuildPro-OGCS Private Limited, we value your privacy and are committed to protecting your personal information. This Privacy Policy outlines how we collect, use, and safeguard your data.
            </p>
            <h3 className="text-sm font-medium text-gray-800 mb-3">1. Information Collection</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>We collect personal information such as your name, contact details, and payment information when you register or make a transaction on our platform.</li>
              <li>We may also collect non-personal information such as your IP address, browser type, and usage patterns to enhance your experience.</li>
            </ul>

            <h3 className="text-sm font-medium text-gray-800 mt-6 mb-3">2. Use of Information</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Personal information is used to process transactions, manage accounts, and provide customer support.</li>
              <li>Non-personal information is used to analyze trends, improve platform functionality, and optimize user experience.</li>
            </ul>

            <h3 className="text-sm font-medium text-gray-800 mt-6 mb-3">3. Data Sharing</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>We do not sell or rent your personal information to third parties.</li>
              <li>We may share information with trusted service providers to facilitate platform operations, subject to strict confidentiality agreements.</li>
            </ul>

            <h3 className="text-sm font-medium text-gray-800 mt-6 mb-3">4. Data Security</h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              We implement industry-standard security measures to protect your data from unauthorized access, alteration, disclosure, or destruction.
            </p>

            <h3 className="text-sm font-medium text-gray-800 mt-6 mb-3">5. User Rights</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>You have the right to access, update, or delete your personal information by contacting us at
                <a href="mailto:privacy@buildpro-ogcs.com" className="text-blue-600 font-medium underline"> support@ogcs.co.in</a>.
              </li>
              <li>You may opt out of receiving promotional emails by clicking the "unsubscribe" link in any email.</li>
            </ul>
          </section>

       {/* Section: Shipping Policy */}
       <section className="mb-8">
            <h2 className="text-sm font-semibold text-gray-800 border-b-2 border-blue-500 mb-4 pb-2">
              Shipping Policy for BuildPro-OGCS Buyer Platform
            </h2>

            <h3 className="text-sm font-medium text-gray-800 mb-3">1. Sale by BuildPro-OGCS</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li><strong>Seller’s Responsibility:</strong> Sellers are responsible for packing, shipping, and delivering products to the customer's address as per the order.</li>
              <li><strong>Shipment Confirmation:</strong> Upon delivery, the customer will receive an OTP. The customer must provide the OTP to the delivery person, who will update the shipment status on the platform with the seller.</li>
              <li><strong>Return Policy:</strong> Refer to the Return Policy for instructions in case the product is damaged or does not meet specified requirements.</li>
              <li><strong>Shipping for Bulk Materials:</strong> For items like cement, steel, aggregates, sand, fly ash, and construction chemicals, the delivery schedule will be mutually agreed upon and begin within 7 days of order confirmation, with completion as per the agreed schedule.</li>
              <li><strong>Shipping for Small Items:</strong> For items like tools and small equipment, delivery is expected within 7 to 15 days of order confirmation.</li>
              <li><strong>Shipping for Specially Manufactured Materials:</strong> For custom materials such as specific chemicals, water stoppers, expansion joints, PEB structures, or formwork materials, manufacturing will begin within 7 days of order confirmation, and delivery will be completed within 45 days.</li>
            </ul>

            <h3 className="text-sm font-medium text-gray-800 mt-6 mb-3">2. Sale by Seller</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li><strong>Seller’s Responsibility:</strong> After a buyer adds a product to their cart, the buyer’s contact information is shared with the seller for further communication. The buyer and seller are responsible for managing the order, payment, and delivery processes. BuildPro-OGCS acts only as a facilitator and is not involved in post-purchase activities.</li>
              <li><strong>Shipping and Delivery Terms:</strong> Shipping and delivery terms will be mutually agreed upon between the buyer and seller. BuildPro-OGCS does not influence these terms.</li>
            </ul>

            <h3 className="text-sm font-medium text-gray-800 mt-6 mb-3">3. Compliance with Indian Laws</h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              This shipping policy complies with all applicable Indian laws to ensure secure, smooth, and legally compliant transactions on the BuildPro-OGCS platform.
            </p>
          </section>
          {/* Section: Limitation of Liability */}
          <section className="mb-8">
            <h2 className="text-sm font-semibold text-gray-800 border-b-2 border-blue-500 mb-4 pb-2">
              Limitation of Liability
            </h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              BuildPro-OGCS and its affiliates shall not be liable for any direct, indirect, incidental, or consequential damages arising from the use of this platform. By using this platform, users agree to resolve disputes directly with the involved parties.
            </p>
          </section>

          {/* Section: Contact Us */}
          <section>
            <h2 className="text-sm font-semibold text-gray-800 border-b-2 border-blue-500 mb-4 pb-2">
              Contact Us
            </h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              For questions or concerns, contact us at
              <a href="mailto:support@buildpro-ogcs.com" className="text-blue-600 font-medium underline"> support@ogcs.co.in</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
